'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Menu, X, User, LogIn, LogOut, LayoutDashboard, ChevronDown, Shield, Settings } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export default function TopbarClient() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user, signOut, getUserProfile } = useAuth();
  const router = useRouter();

  // Fetch user role from profile
  useEffect(() => {
    if (!user) { setUserRole(null); return; }
    getUserProfile()
      .then((profile: any) => setUserRole(profile?.role || user?.user_metadata?.role || 'tenant'))
      .catch(() => setUserRole(user?.user_metadata?.role || 'tenant'));
  }, [user]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = async () => {
    setDropdownOpen(false);
    setMobileOpen(false);
    await signOut();
    router.push('/');
    router.refresh();
  };

  const dashboardHref = userRole === 'admin' ? '/admin' : userRole === 'owner' ? '/owner-dashboard' : '/';
  const dashboardLabel = userRole === 'admin' ? 'Admin Panel' : userRole === 'owner' ? 'My Dashboard' : 'Browse Listings';
  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Account';

  return (
    <>
      {/* Desktop Auth Buttons */}
      <div className="hidden md:flex items-center gap-2">
        {user ? (
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-muted transition-colors text-sm font-semibold text-foreground"
              suppressHydrationWarning
            >
              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                {userRole === 'admin' ? <Shield size={14} className="text-primary" /> : <User size={14} className="text-primary" />}
              </div>
              <span className="max-w-[120px] truncate">{displayName}</span>
              <ChevronDown size={14} className={`text-muted-foreground transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {dropdownOpen && (
              <div className="absolute right-0 top-full mt-2 w-52 bg-card border border-border rounded-2xl shadow-modal z-50 py-1.5 fade-in">
                <div className="px-4 py-2.5 border-b border-border mb-1">
                  <div className="text-xs font-bold text-foreground truncate">{displayName}</div>
                  <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                  {userRole && (
                    <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary capitalize">{userRole}</span>
                  )}
                </div>
                <Link
                  href={dashboardHref}
                  onClick={() => setDropdownOpen(false)}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-colors"
                >
                  <LayoutDashboard size={15} className="text-muted-foreground" />
                  {dashboardLabel}
                </Link>
                <Link
                  href="/account"
                  onClick={() => setDropdownOpen(false)}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-colors"
                >
                  <Settings size={15} className="text-muted-foreground" />
                  My Account
                </Link>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                  suppressHydrationWarning
                >
                  <LogOut size={15} />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            <Link href="/sign-up-login-screen?tab=login" className="btn-ghost text-sm">
              <LogIn size={16} />
              Sign In
            </Link>
            <Link href="/sign-up-login-screen?tab=signup" className="btn-primary text-sm">
              <User size={16} />
              List Property
            </Link>
          </>
        )}
      </div>

      {/* Mobile Login/Signup Button (visible in topbar, unauthenticated only) */}
      {!user && (
        <div className="flex md:hidden items-center gap-1.5">
          <Link
            href="/sign-up-login-screen?tab=login"
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-primary border border-primary/30 hover:bg-primary/5 transition-colors ml-2"
            suppressHydrationWarning
          >
            <LogIn size={15} />
            Sign In
          </Link>
        </div>
      )}

      {/* Mobile Hamburger */}
      <button
        className="md:hidden p-2 rounded-xl hover:bg-muted transition-colors"
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label="Toggle navigation menu"
        aria-expanded={mobileOpen}
        suppressHydrationWarning
      >
        {mobileOpen ? <X size={22} /> : <Menu size={22} />}
      </button>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="absolute top-0 right-0 h-full w-72 bg-card shadow-modal flex flex-col p-6 gap-4 fade-in"
            onClick={(e) => e?.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-extrabold text-lg text-foreground">
                tolettu<span className="text-primary">.com</span>
              </span>
              <button onClick={() => setMobileOpen(false)} className="p-1.5 rounded-lg hover:bg-muted" aria-label="Close menu">
                <X size={20} />
              </button>
            </div>

            {/* User info on mobile */}
            {user && (
              <div className="flex items-center gap-3 p-3 bg-muted rounded-xl">
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                  {userRole === 'admin' ? <Shield size={16} className="text-primary" /> : <User size={16} className="text-primary" />}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-bold text-foreground truncate">{displayName}</div>
                  <div className="text-xs text-muted-foreground capitalize">{userRole}</div>
                </div>
              </div>
            )}

            <nav className="flex flex-col gap-1">
              <Link
                href="/"
                className="px-4 py-3 rounded-xl text-sm font-semibold text-foreground hover:bg-muted transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                Browse Listings
              </Link>
              {user && userRole === 'owner' && (
                <Link
                  href="/owner-dashboard"
                  className="px-4 py-3 rounded-xl text-sm font-semibold text-foreground hover:bg-muted transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  Owner Dashboard
                </Link>
              )}
              {user && userRole === 'admin' && (
                <Link
                  href="/admin"
                  className="px-4 py-3 rounded-xl text-sm font-semibold text-foreground hover:bg-muted transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  Admin Panel
                </Link>
              )}
              {user && (
                <Link
                  href="/account"
                  className="px-4 py-3 rounded-xl text-sm font-semibold text-foreground hover:bg-muted transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  My Account
                </Link>
              )}
              {!user && (
                <Link
                  href="/owner-dashboard"
                  className="px-4 py-3 rounded-xl text-sm font-semibold text-foreground hover:bg-muted transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  Owner Dashboard
                </Link>
              )}
              <Link
                href="/contact"
                className="px-4 py-3 rounded-xl text-sm font-semibold text-foreground hover:bg-muted transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                Contact Us
              </Link>
            </nav>

            <div className="mt-auto flex flex-col gap-2">
              {user ? (
                <button
                  onClick={handleLogout}
                  className="btn-secondary w-full justify-center text-red-600 border-red-200 hover:bg-red-50"
                  suppressHydrationWarning
                >
                  <LogOut size={16} />
                  Sign Out
                </button>
              ) : (
                <>
                  <Link
                    href="/sign-up-login-screen?tab=login"
                    className="btn-secondary w-full justify-center"
                    onClick={() => setMobileOpen(false)}
                  >
                    <LogIn size={16} />
                    Sign In
                  </Link>
                  <Link
                    href="/sign-up-login-screen?tab=signup"
                    className="btn-primary w-full justify-center"
                    onClick={() => setMobileOpen(false)}
                  >
                    <User size={16} />
                    List Your Property
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}